import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@components';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult } from '@utils/apiErrorHandler';
import { PostalCodeSearchResponse, PostalCodeSearchResult } from '../types';

export const MAX_SERVICE_AREA_POSTAL_CODES = 2000;
export const MIN_POSTAL_CODE_QUERY_LENGTH = 2;
const SEARCH_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 300;
const COLLAPSED_CHIP_COUNT = 30;

type SearchStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PostalCodePickerProps {
  id: string;
  value: string[];
  labels: Record<string, string>;
  onChange: (codes: string[], labels: Record<string, string>) => void;
  errorText?: string;
}

const placesLabel = (result: PostalCodeSearchResult) =>
  result.places.map(place => place.name).join(', ');

const placesDetails = (result: PostalCodeSearchResult) =>
  Array.from(
    new Set(result.places.map(place => [place.county, place.state].filter(Boolean).join(', ')))
  )
    .filter(Boolean)
    .join(' · ');

const fetchPostalCodes = async (
  query: string,
  limit: number
): Promise<PostalCodeSearchResponse | null> => {
  const params = new URLSearchParams({ search: query, limit: String(limit) });
  const response = await ApiClient.get(`/agents/postal-codes/?${params.toString()}`);
  const result = await handleApiResult<PostalCodeSearchResponse>(
    response,
    'Failed to search postal codes'
  );
  return result.success && result.data ? result.data : null;
};

const PostalCodePicker: React.FC<PostalCodePickerProps> = ({
  id,
  value,
  labels,
  onChange,
  errorText,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [results, setResults] = useState<PostalCodeSearchResult[]>([]);
  const [count, setCount] = useState(0);
  const [highlighted, setHighlighted] = useState(-1);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef(0);

  const selected = useMemo(() => new Set(value), [value]);
  const sortedCodes = useMemo(() => [...value].sort(), [value]);
  const trimmedQuery = query.trim();
  const queryTooShort = trimmedQuery.length < MIN_POSTAL_CODE_QUERY_LENGTH;
  const listboxId = `${id}-listbox`;

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (queryTooShort) {
      setStatus('idle');
      setResults([]);
      setCount(0);
      return undefined;
    }
    setStatus('loading');
    const timer = setTimeout(async () => {
      try {
        const data = await fetchPostalCodes(trimmedQuery, SEARCH_LIMIT);
        if (requestId !== requestRef.current) return;
        if (!data) {
          setStatus('error');
          return;
        }
        setResults(data.results);
        setCount(data.count);
        setHighlighted(data.results.length > 0 ? 0 : -1);
        setStatus('ready');
      } catch {
        if (requestId === requestRef.current) setStatus('error');
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedQuery, queryTooShort]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const addCodes = (items: PostalCodeSearchResult[]) => {
    const fresh = items.filter(item => !selected.has(item.code));
    const capacity = MAX_SERVICE_AREA_POSTAL_CODES - value.length;
    const accepted = fresh.slice(0, Math.max(capacity, 0));
    setLimitReached(accepted.length < fresh.length);
    if (accepted.length === 0) return;
    const nextLabels = { ...labels };
    accepted.forEach(item => {
      nextLabels[item.code] = placesLabel(item);
    });
    onChange([...value, ...accepted.map(item => item.code)].sort(), nextLabels);
  };

  const removeCode = (code: string) => {
    setLimitReached(false);
    const nextLabels = { ...labels };
    delete nextLabels[code];
    onChange(
      value.filter(item => item !== code),
      nextLabels
    );
  };

  const toggleResult = (item: PostalCodeSearchResult) => {
    if (selected.has(item.code)) {
      removeCode(item.code);
    } else {
      addCodes([item]);
    }
  };

  const unselectedFound = count - results.filter(item => selected.has(item.code)).length;

  const handleAddAll = async () => {
    if (results.length >= count) {
      addCodes(results);
      return;
    }
    setBulkLoading(true);
    try {
      const data = await fetchPostalCodes(trimmedQuery, MAX_SERVICE_AREA_POSTAL_CODES);
      if (data) {
        addCodes(data.results);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleClearAll = () => {
    setLimitReached(false);
    onChange([], {});
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted(prev => (prev + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted(prev => (prev <= 0 ? results.length - 1 : prev - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlighted >= 0 && highlighted < results.length) {
        toggleResult(results[highlighted]);
      }
    }
  };

  const visibleCodes = expanded ? sortedCodes : sortedCodes.slice(0, COLLAPSED_CHIP_COUNT);
  const showDropdown = isOpen && !queryTooShort;

  return (
    <div className="space-y-3">
      <div ref={containerRef} className="relative">
        <Input
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('agents.serviceArea.searchPlaceholder')}
          leftIcon={<span className="material-icons text-base text-gray-400">search</span>}
          state={errorText ? 'error' : 'default'}
          errorText={errorText}
        />

        {showDropdown && (
          <div
            className="absolute left-0 right-0 z-50 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
            data-testid="postal-code-dropdown"
          >
            {status === 'loading' && (
              <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                {t('common.loading')}
              </p>
            )}
            {status === 'error' && (
              <p className="px-3 py-2 text-sm text-error-600 dark:text-error-400">
                {t('agents.serviceArea.searchError')}
              </p>
            )}
            {status === 'ready' && results.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                {t('agents.serviceArea.noResults')}
              </p>
            )}
            {status === 'ready' && results.length > 0 && (
              <>
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-multiselectable
                  className="max-h-64 overflow-y-auto py-1"
                >
                  {results.map((item, index) => {
                    const isSelected = selected.has(item.code);
                    const details = placesDetails(item);
                    return (
                      <li
                        key={item.code}
                        role="option"
                        aria-selected={isSelected}
                        className={`flex cursor-pointer items-start gap-2 px-3 py-2 text-sm ${
                          index === highlighted
                            ? 'bg-primary-50 dark:bg-primary-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        onMouseEnter={() => setHighlighted(index)}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => toggleResult(item)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleResult(item);
                          }
                        }}
                      >
                        <span
                          className={`material-icons mt-0.5 text-base ${
                            isSelected
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        >
                          {isSelected ? 'check_box' : 'check_box_outline_blank'}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-gray-900 dark:text-gray-100">
                            <span className="font-mono">{item.code}</span> – {placesLabel(item)}
                          </span>
                          {details && (
                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                              {details}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2 text-xs dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('agents.serviceArea.resultsSummary', { shown: results.length, count })}
                  </span>
                  {unselectedFound > 0 && (
                    <button
                      type="button"
                      className="font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 dark:text-primary-400"
                      onMouseDown={event => event.preventDefault()}
                      onClick={handleAddAll}
                      disabled={bulkLoading}
                    >
                      {t('agents.serviceArea.addAllFound', { count })}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {queryTooShort && trimmedQuery.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('agents.serviceArea.minQueryHint', { count: MIN_POSTAL_CODE_QUERY_LENGTH })}
        </p>
      )}
      {limitReached && (
        <p className="text-xs text-warning-700 dark:text-warning-300">
          {t('agents.serviceArea.limitReached', { max: MAX_SERVICE_AREA_POSTAL_CODES })}
        </p>
      )}

      {sortedCodes.length > 0 && (
        <div data-testid="postal-code-chips">
          <div className="flex flex-wrap gap-2">
            {visibleCodes.map(code => {
              const label = labels[code];
              return (
                <span
                  key={code}
                  title={label ? undefined : t('agents.serviceArea.unknownCode')}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${
                    label
                      ? 'border-gray-200 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200'
                      : 'border-warning-300 bg-warning-50 text-warning-800 dark:border-warning-700 dark:bg-warning-900/20 dark:text-warning-300'
                  }`}
                >
                  {!label && <span className="material-icons text-sm">warning</span>}
                  <span className="font-mono">{code}</span>
                  <span>– {label || t('agents.serviceArea.unknownCode')}</span>
                  <button
                    type="button"
                    className="ml-0.5 inline-flex rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-100"
                    aria-label={t('agents.serviceArea.removeCode', { code })}
                    onClick={() => removeCode(code)}
                  >
                    <span className="material-icons text-sm">close</span>
                  </button>
                </span>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs">
            {sortedCodes.length > COLLAPSED_CHIP_COUNT && (
              <button
                type="button"
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                onClick={() => setExpanded(prev => !prev)}
              >
                {expanded
                  ? t('agents.serviceArea.showLess')
                  : t('agents.serviceArea.showAll', { count: sortedCodes.length })}
              </button>
            )}
            <button
              type="button"
              className="font-medium text-gray-500 hover:text-error-600 dark:text-gray-400"
              onClick={handleClearAll}
            >
              {t('agents.serviceArea.clearAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { PostalCodePicker };
